import React, { useState, useEffect, useCallback } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import type {
  DragEndEvent as DndKitDragEndEvent,
  DragStartEvent as DndKitDragStartEvent,
  DragOverEvent as DndKitDragOverEvent,
} from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { TreeNode } from "../api/users";

interface ReferralTreeProps {
  data: TreeNode;
  onNodeMove?: (fromUserId: number, toParentId: number) => void;
  onNodeDelete?: (userId: number) => void;
  onNodeLift?: (userId: number) => void;
  onGetAvailableSlots?: (userId: number) => void;
}

// Динамический импорт для избежания ошибок SSR
let Tree: any;
let TreeNodeComponent: any;

// Компонент для drag'n'drop узла
function DraggableTreeNode({
  node,
  onMove,
  onDelete,
  onLift,
  onGetAvailableSlots,
  onToggleCollapse,
  isCollapsed = false,
  isDragging = false,
  isDropTarget = false,
}: {
  node: TreeNode;
  onMove?: (fromUserId: number, toParentId: number) => void;
  onDelete?: (userId: number) => void;
  onLift?: (userId: number) => void;
  onGetAvailableSlots?: (userId: number) => void;
  onToggleCollapse?: (id: number) => void;
  isCollapsed?: boolean;
  isDragging?: boolean;
  isDropTarget?: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isNodeDragging,
  } = useSortable({
    id: node.id.toString(),
    data: {
      type: "user-node",
      node,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isNodeDragging ? 0.5 : 1,
  };

  const handleMove = useCallback(() => {
    if (onMove) {
      // Здесь можно добавить логику для выбора нового родителя
      const newParentId = prompt(
        `Введите ID нового родителя для пользователя ${node.name} (ID: ${node.id}):`
      );
      if (newParentId && !isNaN(Number(newParentId))) {
        onMove(node.id, Number(newParentId));
      }
    }
  }, [node.id, node.name, onMove]);

  const handleDelete = useCallback(() => {
    if (
      onDelete &&
      window.confirm(`Удалить пользователя ${node.name} (ID: ${node.id})?`)
    ) {
      onDelete(node.id);
    }
  }, [node.id, node.name, onDelete]);

  const handleLift = useCallback(() => {
    if (
      onLift &&
      window.confirm(
        `Поднять поддерево для пользователя ${node.name} (ID: ${node.id})?`
      )
    ) {
      onLift(node.id);
    }
  }, [node.id, node.name, onLift]);

  const handleGetAvailableSlots = useCallback(() => {
    if (onGetAvailableSlots) {
      onGetAvailableSlots(node.id);
    }
  }, [node.id, onGetAvailableSlots]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`bg-white rounded shadow px-4 py-2 border-2 text-center min-w-[120px] cursor-move hover:shadow-lg transition-all duration-200 ${
        isDragging ? "opacity-50" : ""
      } ${
        isDropTarget
          ? "border-green-500 bg-green-50 shadow-lg"
          : "border-gray-300 hover:border-blue-400"
      }`}
    >
      <div className="font-bold text-blue-700">{node.name}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400">ID: {node.id}</div>
      {node.path && (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Path: {node.path.join(", ")}
        </div>
      )}
      {node.email && (
        <div className="text-xs text-gray-400 truncate">{node.email}</div>
      )}

      {/* Кнопки действий */}
      <div className="flex justify-center gap-1 mt-2 flex-wrap">
        {onToggleCollapse && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleCollapse(node.id);
            }}
            className="text-xs bg-gray-200 text-gray-800 px-2 py-1 rounded hover:bg-gray-300 transition-colors"
            title={isCollapsed ? "Развернуть" : "Свернуть"}
          >
            {isCollapsed ? "➕" : "➖"}
          </button>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleMove();
          }}
          className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600 transition-colors"
          title="Переместить"
        >
          📁
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleLift();
          }}
          className="text-xs bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600 transition-colors"
          title="Поднять поддерево"
        >
          ⬆️
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDelete();
          }}
          className="text-xs bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600 transition-colors"
          title="Удалить"
        >
          🗑️
        </button>
        {onGetAvailableSlots && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleGetAvailableSlots();
            }}
            className="text-xs bg-purple-500 text-white px-2 py-1 rounded hover:bg-purple-600 transition-colors"
            title="Доступные слоты"
          >
            📊
          </button>
        )}
      </div>
    </div>
  );
}

function ReferralTreeComponent({
  data,
  onNodeMove,
  onNodeDelete,
  onNodeLift,
  onGetAvailableSlots,
}: ReferralTreeProps) {
  const [isClient, setIsClient] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeNode, setActiveNode] = useState<TreeNode | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [collapsedIds, setCollapsedIds] = useState<Set<number>>(new Set());

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    setIsClient(true);

    // Динамически импортируем компоненты только на клиенте
    import("react-organizational-chart")
      .then((module) => {
        Tree = module.Tree;
        TreeNodeComponent = module.TreeNode;
        setIsLoaded(true);
      })
      .catch((error) => {
        console.error(
          "Ошибка загрузки модуля react-organizational-chart:",
          error
        );
      });
  }, []);

  const handleDragStart = (event: DndKitDragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as string);

    // Находим первый активный узел
    const findNode = (node: TreeNode): TreeNode | null => {
      if (node.id.toString() === active.id) return node;
      if (node.children) {
        for (const child of node.children) {
          const found = findNode(child);
          if (found) return found;
        }
      }
      return null;
    };

    const foundNode = findNode(data);
    setActiveNode(foundNode);
  };

  const handleDragOver = (event: DndKitDragOverEvent) => {
    const { over } = event;
    setOverId((over?.id as string) || null);
  };

  const handleDragEnd = (event: DndKitDragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id && over?.id && onNodeMove) {
      const fromUserId = Number(active.id);
      const toParentId = Number(over.id);

      // Проверяем, не пытаемся ли переместить узел в самого себя
      if (fromUserId !== toParentId) {
        onNodeMove(fromUserId, toParentId);
      }
    }

    setActiveId(null);
    setActiveNode(null);
    setOverId(null);
  };

  // Функция для проверки, является ли узел целью для drop
  const isDropTarget = useCallback(
    (nodeId: number) => {
      return overId === nodeId.toString() && activeId !== nodeId.toString();
    },
    [overId, activeId]
  );

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.1, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.1, 0.3));
  };

  const handleZoomReset = () => {
    setZoom(1);
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom((prev) => Math.max(0.3, Math.min(3, prev + delta)));
    }
  };

  const toggleCollapse = useCallback((id: number) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  if (!isClient) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-gray-500 dark:text-gray-400">Загрузка компонента...</div>
      </div>
    );
  }

  if (!isLoaded || !Tree || !TreeNodeComponent) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  function renderNode(node: TreeNode) {
    const isCollapsed = collapsedIds.has(node.id);
    return (
      <TreeNodeComponent
        label={
          <DraggableTreeNode
            node={node}
            onMove={onNodeMove}
            onDelete={onNodeDelete}
            onLift={onNodeLift}
            onGetAvailableSlots={onGetAvailableSlots}
            isDropTarget={isDropTarget(node.id)}
            onToggleCollapse={toggleCollapse}
            isCollapsed={isCollapsed}
          />
        }
        key={node.id}
      >
        {!isCollapsed &&
          Array.isArray(node.children) &&
          node.children.length > 0 &&
          node.children.map((child) => renderNode(child))}
      </TreeNodeComponent>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="relative">
        {/* Кнопки зума */}
        <div className="absolute top-2 right-2 z-10 flex gap-2">
          <button
            onClick={handleZoomOut}
            className="bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-1 rounded shadow border text-sm font-medium transition-colors"
            title="Уменьшить"
          >
            ➖
          </button>
          <button
            onClick={handleZoomReset}
            className="bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-1 rounded shadow border text-sm font-medium transition-colors"
            title="Сбросить зум"
          >
            🔄
          </button>
          <button
            onClick={handleZoomIn}
            className="bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-1 rounded shadow border text-sm font-medium transition-colors"
            title="Увеличить"
          >
            ➕
          </button>
          <div className="bg-white dark:bg-gray-800 px-3 py-1 rounded shadow border text-sm font-medium text-gray-700 dark:text-gray-300">
            {Math.round(zoom * 100)}%
          </div>
        </div>

        <div
          className="overflow-auto p-2 bg-red-50 rounded h-[calc(100vh-400px)]"
          onWheel={handleWheel}
        >
          <div
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: "top left",
              transition: "transform 0.1s ease-out",
            }}
          >
            <Tree
              lineWidth={"2px"}
              lineColor={"#60a5fa"}
              lineBorderRadius={"10px"}
              label={
                <DraggableTreeNode
                  node={data}
                  onMove={onNodeMove}
                  onDelete={onNodeDelete}
                  onLift={onNodeLift}
                  onGetAvailableSlots={onGetAvailableSlots}
                  isDropTarget={isDropTarget(data.id)}
                  onToggleCollapse={toggleCollapse}
                  isCollapsed={collapsedIds.has(data.id)}
                />
              }
            >
              {!collapsedIds.has(data.id) &&
                Array.isArray(data.children) &&
                data.children.length > 0 &&
                data.children.map((child) => renderNode(child))}
            </Tree>
          </div>
        </div>
      </div>

      <DragOverlay>
        {activeId && activeNode ? (
          <div className="bg-white dark:bg-gray-800 rounded shadow-lg px-4 py-2 border-2 border-blue-500 text-center min-w-[120px] opacity-90 transform rotate-3">
            <div className="font-bold text-blue-700">{activeNode.name}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">ID: {activeNode.id}</div>
            {activeNode.path && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Path: {activeNode.path.join(", ")}
              </div>
            )}
            {activeNode.email && (
              <div className="text-xs text-gray-400 truncate">
                {activeNode.email}
              </div>
            )}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export function ReferralTree({
  data,
  onNodeMove,
  onNodeDelete,
  onNodeLift,
  onGetAvailableSlots,
}: ReferralTreeProps) {
  if (!data) {
    return null;
  }

  return (
    <ReferralTreeComponent
      data={data}
      onNodeMove={onNodeMove}
      onNodeDelete={onNodeDelete}
      onNodeLift={onNodeLift}
      onGetAvailableSlots={onGetAvailableSlots}
    />
  );
}
